import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Skeleton — a neutral placeholder block shown while data loads, instead of
 * a blank screen or a spinner. Reserve the same space the real content will
 * occupy so the layout doesn't shift when data arrives. The shimmer respects
 * `prefers-reduced-motion` (pulse only under motion-safe).
 */
export type SkeletonProps = React.HTMLAttributes<HTMLDivElement>;

export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "rounded-md bg-white-warm motion-safe:animate-pulse",
        className,
      )}
      {...props}
    />
  );
}

/**
 * SkeletonList — placeholder rows for a loading list/table, so a tab shows
 * structure instead of a bare "Loading…" line and the layout doesn't shift
 * when rows arrive.
 */
export function SkeletonList({
  rows = 4,
  className,
  "data-testid": testId,
}: {
  rows?: number;
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <div
      className={cn("flex flex-col gap-space-2", className)}
      aria-busy="true"
      aria-label="Loading"
      data-testid={testId}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}

/**
 * SkeletonText — a stack of skeleton lines approximating a paragraph; the
 * last line is shortened so it reads as text.
 */
export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-space-2", className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn("h-4", i === lines - 1 ? "w-2/3" : "w-full")}
        />
      ))}
    </div>
  );
}
